// Public event detail page. Shows a local event's full details, attachments,
// a QR card, and a 21+ confirmation interstitial when required. If this device
// created the event, a "Manage" link is offered.

import { ApiError, getEventDetail } from '../lib/api';
import { categoryMeta } from '../lib/categories';
import { formatDateTime, formatPrice } from '../lib/format';
import { forgetOwned, listOwned } from '../lib/ownership';
import { navigate } from '../lib/router';
import { buildQrCard } from './qrCard';
import type { AttachmentMeta, LocalEventDetail } from '../types/event';

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function renderEventDetailPage(root: HTMLElement, params: Record<string, string>): void {
  const id = params.id;
  root.innerHTML = `<section class="page"><a class="back-link" href="/" data-link>&larr; Back to events</a><div class="placeholder">Loading event...</div></section>`;

  getEventDetail(id)
    .then((ev) => renderLoaded(root, ev))
    .catch((err) => {
      // A 404 means the event is gone; drop any stale "Your events" entry.
      if (err instanceof ApiError && err.status === 404) forgetOwned(id);
      const msg = err instanceof ApiError && err.status === 404 ? 'This event was not found. It may have been removed.' : 'Could not load this event.';
      root.innerHTML = `<section class="page"><a class="back-link" href="/" data-link>&larr; Back to events</a><div class="placeholder error">${esc(msg)}</div></section>`;
    });
}

function attachmentNode(a: AttachmentMeta): HTMLElement {
  if (a.kind === 'image') {
    const img = document.createElement('img');
    img.className = 'attach-image';
    img.src = a.url;
    img.alt = a.filename;
    img.loading = 'lazy';
    return img;
  }
  const link = document.createElement('a');
  link.className = 'attach-doc';
  link.href = a.url;
  link.textContent = `\u{1F4CE} ${a.filename}`;
  link.rel = 'noopener noreferrer';
  return link;
}

function renderLoaded(root: HTMLElement, ev: LocalEventDetail): void {
  const meta = categoryMeta(ev.category);
  const owned = listOwned().some((o) => o.id === ev.sourceId || o.id === ev.id);
  const price = formatPrice(ev.priceMin, ev.priceMax);
  const venue = [ev.venueName, ev.address].filter(Boolean).join(' · ');

  root.innerHTML = `
    <section class="page detail-page">
      <a class="back-link" href="/" data-link>&larr; Back to events</a>
      <article class="detail">
        <div class="detail-head">
          <span class="card-cat" style="--chip:${meta.color}">${meta.emoji} ${meta.label}</span>
          ${ev.age21Plus ? '<span class="badge-21">21+</span>' : ''}
        </div>
        <h1 class="detail-title">${esc(ev.title)}</h1>
        <p class="detail-when">${esc(formatDateTime(ev.start))}${ev.end ? ' to ' + esc(formatDateTime(ev.end)) : ''}</p>
        ${venue ? `<p class="detail-venue">${esc(venue)}</p>` : ''}
        ${price ? `<p class="detail-price">${esc(price)}</p>` : ''}
        ${ev.description ? `<p class="detail-desc">${esc(ev.description)}</p>` : ''}
        <div id="attach-mount" class="attach-grid"></div>
        <div class="detail-actions">
          ${owned ? `<a class="btn primary" id="manage-link" href="#">Manage this event</a>` : ''}
        </div>
        <div id="qr-mount" class="qr-mount"></div>
      </article>
    </section>
  `;

  const attachMount = root.querySelector<HTMLElement>('#attach-mount')!;
  for (const a of ev.attachments ?? []) attachMount.appendChild(attachmentNode(a));

  root.querySelector<HTMLElement>('#qr-mount')!.appendChild(
    buildQrCard({ name: ev.title, start: ev.start, publicUrl: ev.sourceUrl })
  );

  if (owned) {
    const o = listOwned().find((x) => x.id === ev.sourceId || x.id === ev.id);
    const link = root.querySelector<HTMLAnchorElement>('#manage-link');
    if (link && o) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        // o.manageUrl is absolute; route to its path so the SPA handles it.
        navigate(new URL(o.manageUrl).pathname);
      });
    }
  }

  if (ev.age21Plus) maybeGate(ev.sourceId || ev.id);
}

// One-time 21+ confirmation per event per session.
function maybeGate(key: string): void {
  const flag = `irl.age21.${key}`;
  if (sessionStorage.getItem(flag) === '1') return;

  const overlay = document.createElement('div');
  overlay.className = 'age-gate';
  overlay.innerHTML = `
    <div class="age-gate-box" role="dialog" aria-modal="true" aria-label="Age confirmation">
      <h2>21+ event</h2>
      <p>This event is restricted to attendees 21 or older. Please confirm your age to view the details.</p>
      <div class="age-gate-actions">
        <button class="btn primary" id="age-yes" type="button">I am 21 or older</button>
        <a class="btn ghost" href="/" data-link id="age-no">Take me back</a>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector<HTMLButtonElement>('#age-yes')!.addEventListener('click', () => {
    try {
      sessionStorage.setItem(flag, '1');
    } catch {
      /* ignore */
    }
    overlay.remove();
  });
  overlay.querySelector<HTMLAnchorElement>('#age-no')!.addEventListener('click', (e) => {
    e.preventDefault();
    overlay.remove();
    navigate('/');
  });
}
